import {
  Renderer,
  Stave,
  StaveNote,
  Voice,
  Formatter,
  StaveConnector,
  Accidental,
  Beam,
  Dot,
} from 'vexflow';
import { MidiNote, TimeSignature, DetectedChord } from '../types/midi';

export interface ScoreTranscriptionOptions {
  splitPitch: number; // default 60 (Middle C / C4)
  smartBass?: boolean; // default true: when bass stave is empty, routes lowest sustained note to bass
  polyphonyMode?: 'auto' | 'singleVoice'; // default 'auto': separates overlapping sustained notes into voice 2
  keySignature: string; // 'C', 'G', 'D', 'A', 'E', 'F', 'Bb', 'Eb'
  gridResolution: '1/4' | '1/8' | '1/16'; // default '1/8'
  measuresPerRow: number; // default 2 or 3
  scale: number; // 0.8 to 1.4
  startMeasureIndex?: number; // 0-based start measure for page-by-page display
  maxMeasuresToRender?: number; // count of measures to display on this page
}

export interface MeasureBoundingBox {
  measureIndex: number;
  startTick: number;
  endTick: number;
  x: number;
  y: number;
  width: number;
  height: number;
  noteStartX?: number;
  noteEndX?: number;
}

const FLAT_KEYS = new Set(['F', 'Bb', 'Eb', 'Ab', 'Db', 'Gb', 'Dm', 'Gm', 'Cm', 'Fm']);

// Scale notes in each key for smart accidental suppression
const KEY_ACCIDENTALS: Record<string, Record<string, string>> = {
  C: {},
  G: { f: '#' },
  D: { f: '#', c: '#' },
  A: { f: '#', c: '#', g: '#' },
  E: { f: '#', c: '#', g: '#', d: '#' },
  F: { b: 'b' },
  Bb: { b: 'b', e: 'b' },
  Eb: { b: 'b', e: 'b', a: 'b' },
  Ab: { b: 'b', e: 'b', a: 'b', d: 'b' },
};

export function pitchToVexKey(pitch: number, keySignature: string = 'C') {
  const isFlatKey = FLAT_KEYS.has(keySignature);
  const noteNamesSharp = ['c', 'c#', 'd', 'd#', 'e', 'f', 'f#', 'g', 'g#', 'a', 'a#', 'b'];
  const noteNamesFlat = ['c', 'db', 'd', 'eb', 'e', 'f', 'gb', 'g', 'ab', 'a', 'bb', 'b'];

  const octave = Math.floor(pitch / 12) - 1;
  const rawName = isFlatKey ? noteNamesFlat[pitch % 12] : noteNamesSharp[pitch % 12];
  const step = rawName[0];
  const accChar = rawName.length > 1 ? rawName[1] : undefined;

  // Check if accidental is already implied by key signature
  const keyMap = KEY_ACCIDENTALS[keySignature] || {};
  const expectedAcc = keyMap[step];

  let displayAccidental: string | undefined = undefined;
  if (accChar) {
    if (accChar !== expectedAcc) {
      displayAccidental = accChar; // Needs sharp or flat symbol
    }
  } else if (expectedAcc) {
    displayAccidental = 'n'; // Natural needed to cancel key signature
  }

  return {
    key: `${rawName}/${octave}`,
    step,
    accidental: displayAccidental,
    octave,
    pitch,
  };
}

/**
 * Transcribe a slice of notes into VexFlow StaveNote objects for a single stave
 */
function buildStaveNotesForMeasure(
  measureNotes: MidiNote[],
  clef: 'treble' | 'bass',
  measureStartTick: number,
  ticksPerMeasure: number,
  _ticksPerBeat: number,
  beatsPerMeasure: number,
  gridResolution: '1/4' | '1/8' | '1/16',
  keySignature: string,
  stemDirection?: 1 | -1
): StaveNote[] {
  const subdivsPerBeat = gridResolution === '1/16' ? 4 : gridResolution === '1/8' ? 2 : 1;
  const totalSlots = beatsPerMeasure * subdivsPerBeat;
  const ticksPerSlot = ticksPerMeasure / totalSlots;

  // If no notes in this stave, return a full measure rest
  if (measureNotes.length === 0) {
    return [
      new StaveNote({
        clef,
        keys: [clef === 'treble' ? 'b/4' : 'd/3'],
        duration: 'wr',
      }),
    ];
  }

  // Assign notes to slot indices
  const slots: (MidiNote[] | null)[] = new Array(totalSlots).fill(null);
  for (const note of measureNotes) {
    const relTick = Math.max(0, Math.min(ticksPerMeasure - 1, note.startTick - measureStartTick));
    const slotIdx = Math.floor(relTick / ticksPerSlot);
    if (slotIdx >= 0 && slotIdx < totalSlots) {
      if (!slots[slotIdx]) slots[slotIdx] = [];
      slots[slotIdx]!.push(note);
    }
  }

  // Parse into continuous sequence of notes and rests
  const result: StaveNote[] = [];
  let currentSlot = 0;

  while (currentSlot < totalSlots) {
    const notesInSlot = slots[currentSlot];

    if (!notesInSlot || notesInSlot.length === 0) {
      // Find length of silence
      let silenceSlots = 0;
      while (currentSlot + silenceSlots < totalSlots && !slots[currentSlot + silenceSlots]) {
        silenceSlots++;
      }

      // Convert silence slots to standard rest values
      let remaining = silenceSlots;
      while (remaining > 0) {
        let durStr = '16r';
        let slotsUsed = 1;

        if (subdivsPerBeat === 1) {
          // Quarter note slots
          if (remaining >= 4 && currentSlot % 4 === 0) {
            durStr = 'wr';
            slotsUsed = 4;
          } else if (remaining >= 2 && currentSlot % 2 === 0) {
            durStr = 'hr';
            slotsUsed = 2;
          } else {
            durStr = 'qr';
            slotsUsed = 1;
          }
        } else if (subdivsPerBeat === 2) {
          // Eighth note slots (2 slots per beat)
          if (remaining >= 8 && currentSlot % 8 === 0) {
            durStr = 'wr';
            slotsUsed = 8;
          } else if (remaining >= 4 && currentSlot % 4 === 0) {
            durStr = 'hr';
            slotsUsed = 4;
          } else if (remaining >= 2 && currentSlot % 2 === 0) {
            durStr = 'qr';
            slotsUsed = 2;
          } else {
            durStr = '8r';
            slotsUsed = 1;
          }
        } else {
          // Sixteenth note slots (4 slots per beat)
          if (remaining >= 16 && currentSlot % 16 === 0) {
            durStr = 'wr';
            slotsUsed = 16;
          } else if (remaining >= 8 && currentSlot % 8 === 0) {
            durStr = 'hr';
            slotsUsed = 8;
          } else if (remaining >= 4 && currentSlot % 4 === 0) {
            durStr = 'qr';
            slotsUsed = 4;
          } else if (remaining >= 2 && currentSlot % 2 === 0) {
            durStr = '8r';
            slotsUsed = 2;
          } else {
            durStr = '16r';
            slotsUsed = 1;
          }
        }

        const restNote = new StaveNote({
          clef,
          keys: [clef === 'treble' ? 'b/4' : 'd/3'],
          duration: durStr,
        });
        result.push(restNote);
        currentSlot += slotsUsed;
        remaining -= slotsUsed;
      }
    } else {
      // Look ahead to find the next slot with note onsets in this measure
      let nextNoteSlot = totalSlots;
      for (let s = currentSlot + 1; s < totalSlots; s++) {
        if (slots[s] && slots[s]!.length > 0) {
          nextNoteSlot = s;
          break;
        }
      }
      const maxAvailableSlots = nextNoteSlot - currentSlot;

      // Determine duration from maximum note duration in slot
      const maxDurTicks = Math.max(...notesInSlot.map((n) => n.durationTicks));
      const requestedSlots = Math.max(1, Math.round(maxDurTicks / ticksPerSlot));
      const durInSlots = Math.min(maxAvailableSlots, requestedSlots);

      // Map duration slots to notation duration with dotted note detection
      let durStr = 'q';
      let actualSlots = 1;
      let isDotted = false;

      if (subdivsPerBeat === 1) {
        if (durInSlots >= 4) {
          durStr = 'w';
          actualSlots = 4;
        } else if (durInSlots >= 3) {
          durStr = 'h';
          actualSlots = 3;
          isDotted = true;
        } else if (durInSlots >= 2) {
          durStr = 'h';
          actualSlots = 2;
        } else {
          durStr = 'q';
          actualSlots = 1;
        }
      } else if (subdivsPerBeat === 2) {
        if (durInSlots >= 8) {
          durStr = 'w';
          actualSlots = 8;
        } else if (durInSlots >= 6) {
          durStr = 'h';
          actualSlots = 6;
          isDotted = true;
        } else if (durInSlots >= 4) {
          durStr = 'h';
          actualSlots = 4;
        } else if (durInSlots >= 3) {
          durStr = 'q';
          actualSlots = 3;
          isDotted = true;
        } else if (durInSlots >= 2) {
          durStr = 'q';
          actualSlots = 2;
        } else {
          durStr = '8';
          actualSlots = 1;
        }
      } else {
        if (durInSlots >= 16) {
          durStr = 'w';
          actualSlots = 16;
        } else if (durInSlots >= 12) {
          durStr = 'h';
          actualSlots = 12;
          isDotted = true;
        } else if (durInSlots >= 8) {
          durStr = 'h';
          actualSlots = 8;
        } else if (durInSlots >= 6) {
          durStr = 'q';
          actualSlots = 6;
          isDotted = true;
        } else if (durInSlots >= 4) {
          durStr = 'q';
          actualSlots = 4;
        } else if (durInSlots >= 3) {
          durStr = '8';
          actualSlots = 3;
          isDotted = true;
        } else if (durInSlots >= 2) {
          durStr = '8';
          actualSlots = 2;
        } else {
          durStr = '16';
          actualSlots = 1;
        }
      }

      // Ensure we never jump past next note onset or measure boundary
      actualSlots = Math.min(actualSlots, maxAvailableSlots);

      // Convert MIDI pitches to VexFlow keys, sorted from lowest to highest
      const uniquePitches = Array.from(new Set(notesInSlot.map((n) => n.pitch))).sort((a, b) => a - b);
      const parsedKeys = uniquePitches.map((p) => pitchToVexKey(p, keySignature));

      const staveNote = new StaveNote({
        clef,
        keys: parsedKeys.map((k) => k.key),
        duration: durStr,
        autoStem: stemDirection === undefined,
      });

      if (stemDirection !== undefined) {
        staveNote.setStemDirection(stemDirection);
      }

      if (isDotted) {
        staveNote.addModifier(new Dot(), 0);
      }

      // Add accidental modifiers
      parsedKeys.forEach((k, idx) => {
        if (k.accidental) {
          staveNote.addModifier(new Accidental(k.accidental), idx);
        }
      });

      result.push(staveNote);
      currentSlot += actualSlots;
    }
  }

  return result;
}

export interface RenderScoreResult {
  boundingBoxes: MeasureBoundingBox[];
  totalHeight: number;
  totalMeasures: number;
}

/**
 * Helper to render notes on a stave either as two polyphonic voices (upper + lower)
 * or as a single voice when no duration overlap exists.
 */
function renderStaveVoices(
  notes: MidiNote[],
  clef: 'treble' | 'bass',
  stave: Stave,
  context: any,
  startTick: number,
  ticksPerMeasure: number,
  ticksPerBeat: number,
  beatsPerMeasure: number,
  beatValue: number,
  gridResolution: '1/4' | '1/8' | '1/16',
  keySignature: string,
  width: number,
  polyphonyMode?: 'auto' | 'singleVoice'
) {
  if (notes.length === 0) {
    const rest = new StaveNote({
      clef,
      keys: [clef === 'treble' ? 'b/4' : 'd/3'],
      duration: 'wr',
    });
    const voice = new Voice({ numBeats: beatsPerMeasure, beatValue }).setMode(Voice.Mode.SOFT);
    voice.addTickables([rest]);
    new Formatter().joinVoices([voice]).format([voice], width);
    voice.draw(context, stave);
    return;
  }

  // Check for polyphonic overlap (a note is sustained into subsequent note onsets)
  const sorted = [...notes].sort((a, b) => a.startTick - b.startTick || a.pitch - b.pitch);
  let hasOverlap = false;
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i].startTick + sorted[i].durationTicks > sorted[i + 1].startTick + 40) {
      hasOverlap = true;
      break;
    }
  }

  if (polyphonyMode !== 'singleVoice' && hasOverlap && notes.length >= 2) {
    // Partition notes into sustained/lower voice and moving upper voice
    const lowerNotes: MidiNote[] = [];
    const upperNotes: MidiNote[] = [];

    for (let i = 0; i < sorted.length; i++) {
      const n = sorted[i];
      const overlapsFuture = sorted.some(
        (o, j) => j > i && o.startTick < n.startTick + n.durationTicks - 40
      );
      if (overlapsFuture) {
        lowerNotes.push(n);
      } else {
        upperNotes.push(n);
      }
    }

    if (lowerNotes.length > 0 && upperNotes.length > 0) {
      try {
        const vUpperNotes = buildStaveNotesForMeasure(
          upperNotes,
          clef,
          startTick,
          ticksPerMeasure,
          ticksPerBeat,
          beatsPerMeasure,
          gridResolution,
          keySignature,
          1 // stem up
        );

        const vLowerNotes = buildStaveNotesForMeasure(
          lowerNotes,
          clef,
          startTick,
          ticksPerMeasure,
          ticksPerBeat,
          beatsPerMeasure,
          gridResolution,
          keySignature,
          -1 // stem down
        );

        const vUpper = new Voice({ numBeats: beatsPerMeasure, beatValue }).setMode(Voice.Mode.SOFT);
        const vLower = new Voice({ numBeats: beatsPerMeasure, beatValue }).setMode(Voice.Mode.SOFT);

        vUpper.addTickables(vUpperNotes);
        vLower.addTickables(vLowerNotes);

        const formatter = new Formatter();
        formatter.joinVoices([vUpper, vLower]);
        formatter.format([vUpper, vLower], width);

        vUpper.draw(context, stave);
        vLower.draw(context, stave);

        const upperBeams = Beam.generateBeams(vUpperNotes.filter((n) => !n.isRest()));
        upperBeams.forEach((b) => b.setContext(context).draw());

        const lowerBeams = Beam.generateBeams(vLowerNotes.filter((n) => !n.isRest()));
        lowerBeams.forEach((b) => b.setContext(context).draw());

        return;
      } catch (polyErr) {
        console.warn('[ScoreTranscriber] Polyphony fallback to single voice:', polyErr);
      }
    }
  }

  // Single Voice Fallback
  const vexNotes = buildStaveNotesForMeasure(
    notes,
    clef,
    startTick,
    ticksPerMeasure,
    ticksPerBeat,
    beatsPerMeasure,
    gridResolution,
    keySignature
  );

  if (vexNotes.length > 0) {
    const voice = new Voice({ numBeats: beatsPerMeasure, beatValue }).setMode(Voice.Mode.SOFT);
    voice.addTickables(vexNotes);
    const beams = Beam.generateBeams(vexNotes.filter((n) => !n.isRest()));
    new Formatter().joinVoices([voice]).format([voice], width);
    voice.draw(context, stave);
    beams.forEach((b) => b.setContext(context).draw());
  }
}

/**
 * Render Piano Grand Staff (Treble + Bass + Brace + Barlines + Notes)
 */
export function renderPianoScore(
  container: HTMLDivElement,
  notes: MidiNote[],
  timeSignature: TimeSignature,
  ppq: number,
  options: ScoreTranscriptionOptions,
  _activeMeasureNumber?: number,
  measureChords?: Map<number, DetectedChord[]>
): RenderScoreResult {
  // Clear previous rendering
  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }

  const beatsPerMeasure = timeSignature.beats;
  const beatValue = timeSignature.beatType;
  const ticksPerBeat = ppq * (4 / beatValue);
  const ticksPerMeasure = ticksPerBeat * beatsPerMeasure;

  // Calculate highest measure with notes, at least 4 measures
  let maxTick = 0;
  for (const n of notes) {
    const end = n.startTick + n.durationTicks;
    if (end > maxTick) maxTick = end;
  }
  const calculatedMeasures = Math.max(4, Math.ceil(maxTick / ticksPerMeasure) || 4);
  const totalMeasures = Math.min(64, calculatedMeasures);

  // Pagination support: render either a single page slice or all measures
  const startMeasure = options.startMeasureIndex !== undefined ? Math.max(0, options.startMeasureIndex) : 0;
  const countToRender = options.maxMeasuresToRender !== undefined ? options.maxMeasuresToRender : totalMeasures;
  const endMeasure = Math.min(totalMeasures, startMeasure + countToRender);
  const measuresOnPage = Math.max(1, endMeasure - startMeasure);

  const rawWidth = container.clientWidth > 0 ? container.clientWidth : 750;
  const containerWidth = Math.max(320, Math.min(1200, rawWidth));
  const requestedMeasuresPerRow = Math.max(1, Math.min(4, options.measuresPerRow || 2));
  const measuresPerRow = containerWidth < 500 ? 1 : (containerWidth < 680 && requestedMeasuresPerRow > 2 ? 2 : requestedMeasuresPerRow);
  const numRows = Math.ceil(measuresOnPage / measuresPerRow);

  const rowMarginLeft = containerWidth < 460 ? 24 : 40;
  const rowMarginRight = containerWidth < 460 ? 16 : 30;
  const usableWidth = Math.max(260, containerWidth - rowMarginLeft - rowMarginRight);
  const measureWidth = Math.floor(usableWidth / measuresPerRow);

  const staveHeight = 90;
  const staveSpacing = 70; // gap between treble and bass stave
  const systemHeight = staveHeight + staveSpacing + 80; // gap between systems
  const totalHeight = numRows * systemHeight + 60;

  const renderer = new Renderer(container, Renderer.Backends.SVG);
  renderer.resize(containerWidth, totalHeight);
  const context = renderer.getContext();
  context.setFont('Arial', 10);

  const boundingBoxes: MeasureBoundingBox[] = [];

  for (let idx = 0; idx < measuresOnPage; idx++) {
    const m = startMeasure + idx;
    const measureIndex = m;
    const measureNumber = m + 1;
    const rowIdx = Math.floor(idx / measuresPerRow);
    const colIdx = idx % measuresPerRow;

    const startTick = m * ticksPerMeasure;
    const endTick = (m + 1) * ticksPerMeasure;

    const x = rowMarginLeft + colIdx * measureWidth;
    const yTreble = 30 + rowIdx * systemHeight;
    const yBass = yTreble + staveSpacing;

    // Filter notes for this measure (notes that start within this measure)
    const notesInMeasure = notes.filter(
      (n) => n.startTick >= startTick && n.startTick < endTick
    );

    let trebleNotes = notesInMeasure.filter((n) => n.pitch >= options.splitPitch);
    let bassNotes = notesInMeasure.filter((n) => n.pitch < options.splitPitch);

    // Smart Bass Routing:
    // If the bass stave is empty, but treble has multiple notes and the lowest note
    // is a bass accompaniment or sustained pedal note (pitch <= 64 or duration >= ticksPerBeat),
    // route that lowest sustained note into the bass stave!
    if (options.smartBass !== false && bassNotes.length === 0 && trebleNotes.length > 1) {
      const sortedByPitch = [...trebleNotes].sort((a, b) => a.pitch - b.pitch || a.startTick - b.startTick);
      const lowestNote = sortedByPitch[0];
      if (lowestNote.pitch <= 64 || lowestNote.durationTicks >= ticksPerBeat) {
        bassNotes = [lowestNote];
        trebleNotes = trebleNotes.filter((n) => n.id !== lowestNote.id);
      }
    }

    // Staves
    const trebleStave = new Stave(x, yTreble, measureWidth);
    const bassStave = new Stave(x, yBass, measureWidth);

    // First measure of a row gets clef, key signature, and time signature
    if (colIdx === 0) {
      trebleStave.addClef('treble');
      bassStave.addClef('bass');

      if (options.keySignature && options.keySignature !== 'C') {
        trebleStave.addKeySignature(options.keySignature);
        bassStave.addKeySignature(options.keySignature);
      }

      if (m === 0 || (idx === 0 && startMeasure > 0)) {
        trebleStave.addTimeSignature(`${beatsPerMeasure}/${beatValue}`);
        bassStave.addTimeSignature(`${beatsPerMeasure}/${beatValue}`);
      }
    }

    // Set measure number & chord symbol badge on top of treble stave
    const chords = measureChords?.get(m) || [];
    const chordBadge = chords.length > 0 ? ` • ${chords.map((c) => c.symbol).join(' - ')}` : '';
    const badgeText = `${measureNumber}${chordBadge}`;
    trebleStave.setSection(badgeText, 0);

    // Draw staves
    trebleStave.setContext(context).draw();
    bassStave.setContext(context).draw();

    // Piano Grand Staff Connectors on first measure of each row
    if (colIdx === 0) {
      const brace = new StaveConnector(trebleStave, bassStave);
      brace.setType(StaveConnector.type.BRACE);
      brace.setContext(context).draw();

      const leftLine = new StaveConnector(trebleStave, bassStave);
      leftLine.setType(StaveConnector.type.SINGLE_LEFT);
      leftLine.setContext(context).draw();
    }

    // Connect right barline for both staves
    const rightLine = new StaveConnector(trebleStave, bassStave);
    rightLine.setType(StaveConnector.type.SINGLE_RIGHT);
    rightLine.setContext(context).draw();

    // Format & Draw Treble Notes (with multi-voice polyphony support)
    const effectiveWidth = measureWidth - (colIdx === 0 ? 70 : 25);
    renderStaveVoices(
      trebleNotes,
      'treble',
      trebleStave,
      context,
      startTick,
      ticksPerMeasure,
      ticksPerBeat,
      beatsPerMeasure,
      beatValue,
      options.gridResolution,
      options.keySignature,
      effectiveWidth,
      options.polyphonyMode
    );

    // Format & Draw Bass Notes (with multi-voice polyphony support)
    renderStaveVoices(
      bassNotes,
      'bass',
      bassStave,
      context,
      startTick,
      ticksPerMeasure,
      ticksPerBeat,
      beatsPerMeasure,
      beatValue,
      options.gridResolution,
      options.keySignature,
      effectiveWidth,
      options.polyphonyMode
    );

    let noteStartX = x;
    let noteEndX = x + measureWidth;
    try {
      if (typeof trebleStave.getNoteStartX === 'function') {
        noteStartX = trebleStave.getNoteStartX();
      }
      if (typeof trebleStave.getNoteEndX === 'function') {
        noteEndX = trebleStave.getNoteEndX();
      }
    } catch {
      // fallback to full stave bounds
    }

    boundingBoxes.push({
      measureIndex,
      startTick,
      endTick,
      x,
      y: yTreble - 15,
      width: measureWidth,
      height: staveSpacing + staveHeight + 25,
      noteStartX,
      noteEndX,
    });
  }

  return {
    boundingBoxes,
    totalHeight,
    totalMeasures,
  };
}

/**
 * Generate standard MusicXML file format for MuseScore, Sibelius, Dorico, Finale
 */
export function generateMusicXML(
  notes: Omit<MidiNote, 'id'>[],
  timeSignature: TimeSignature,
  ppq: number,
  bpm: number = 120,
  keySignature: string = 'C',
  splitPitch: number = 60
): string {
  const beatsPerMeasure = timeSignature.beats;
  const beatType = timeSignature.beatType;
  const ticksPerBeat = ppq * (4 / beatType);
  const ticksPerMeasure = ticksPerBeat * beatsPerMeasure;

  let maxTick = 0;
  for (const n of notes) {
    const end = n.startTick + n.durationTicks;
    if (end > maxTick) maxTick = end;
  }
  const totalMeasures = Math.max(1, Math.ceil(maxTick / ticksPerMeasure) || 1);

  let xml = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 3.1 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">
<score-partwise version="3.1">
  <work>
    <work-title>Piano Transcription</work-title>
  </work>
  <part-list>
    <score-part id="P1">
      <part-name>Piano</part-name>
      <part-abbreviation>Pno.</part-abbreviation>
      <score-instrument id="P1-I1">
        <instrument-name>Acoustic Grand Piano</instrument-name>
      </score-instrument>
      <midi-instrument id="P1-I1">
        <midi-program>1</midi-program>
      </midi-instrument>
    </score-part>
  </part-list>
  <part id="P1">
`;

  for (let m = 0; m < totalMeasures; m++) {
    const mNum = m + 1;
    const startTick = m * ticksPerMeasure;
    const endTick = (m + 1) * ticksPerMeasure;

    xml += `    <measure number="${mNum}">\n`;

    if (m === 0) {
      xml += `      <attributes>
        <divisions>${Math.round(ppq)}</divisions>
        <key>
          <fifths>${getFifths(keySignature)}</fifths>
        </key>
        <time>
          <beats>${beatsPerMeasure}</beats>
          <beat-type>${beatType}</beat-type>
        </time>
        <staves>2</staves>
        <clef number="1">
          <sign>G</sign>
          <line>2</line>
        </clef>
        <clef number="2">
          <sign>F</sign>
          <line>4</line>
        </clef>
      </attributes>
      <direction placement="above">
        <direction-type>
          <metronome>
            <beat-unit>quarter</beat-unit>
            <per-minute>${bpm}</per-minute>
          </metronome>
        </direction-type>
        <sound tempo="${bpm}"/>
      </direction>
`;
    }

    const notesInMeasure = notes
      .filter((n) => n.startTick >= startTick && n.startTick < endTick)
      .sort((a, b) => a.startTick - b.startTick || a.pitch - b.pitch);

    if (notesInMeasure.length === 0) {
      // Whole rest
      xml += `      <note>
        <rest measure="yes"/>
        <duration>${Math.round(ticksPerMeasure)}</duration>
        <voice>1</voice>
        <staff>1</staff>
      </note>
      <note>
        <rest measure="yes"/>
        <duration>${Math.round(ticksPerMeasure)}</duration>
        <voice>2</voice>
        <staff>2</staff>
      </note>
`;
    } else {
      for (const note of notesInMeasure) {
        const staff = note.pitch >= splitPitch ? 1 : 2;
        const noteInfo = pitchToVexKey(note.pitch, keySignature);
        const step = noteInfo.step.toUpperCase();
        const alter = noteInfo.accidental === '#' ? 1 : noteInfo.accidental === 'b' ? -1 : 0;
        const dur = Math.max(1, Math.round(note.durationTicks));

        xml += `      <note>
        <pitch>
          <step>${step}</step>
          ${alter !== 0 ? `<alter>${alter}</alter>` : ''}
          <octave>${noteInfo.octave}</octave>
        </pitch>
        <duration>${dur}</duration>
        <voice>${staff}</voice>
        <type>${dur >= ppq * 2 ? 'half' : dur >= ppq ? 'quarter' : 'eighth'}</type>
        <staff>${staff}</staff>
      </note>
`;
      }
    }

    xml += `    </measure>\n`;
  }

  xml += `  </part>
</score-partwise>`;

  return xml;
}

function getFifths(key: string): number {
  const fifthsMap: Record<string, number> = {
    C: 0,
    G: 1,
    D: 2,
    A: 3,
    E: 4,
    B: 5,
    'F#': 6,
    F: -1,
    Bb: -2,
    Eb: -3,
    Ab: -4,
    Db: -5,
  };
  return fifthsMap[key] || 0;
}

/**
 * Trigger browser download of MusicXML file
 */
export function downloadMusicXMLFile(
  notes: Omit<MidiNote, 'id'>[],
  timeSignature: TimeSignature,
  ppq: number,
  bpm: number = 120,
  keySignature: string = 'C',
  splitPitch: number = 60,
  filename: string = `piano_transcription_${Date.now()}.musicxml`
) {
  const xmlContent = generateMusicXML(notes, timeSignature, ppq, bpm, keySignature, splitPitch);
  const blob = new Blob([xmlContent], { type: 'application/vnd.recordare.musicxml+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
